import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import TextField from "aldoo-ra/TextField"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import DataTable from "aldoo-ra/DataTable"
import Modal from "aldoo-ra/Modal"
import Tabs from "aldoo-ra/Tabs"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Typography from "aldoo-ra/Typography"
import { TrashIcon, EnvelopeIcon, ChatBubbleLeftRightIcon, DevicePhoneMobileIcon, PhoneIcon } from "@heroicons/react/24/outline"

const KIND_ICON = {
  email: EnvelopeIcon,
  slack: ChatBubbleLeftRightIcon,
  sms: DevicePhoneMobileIcon,
  voice: PhoneIcon,
}

const KindLine = ({ kind, label }) => {
  const Icon = KIND_ICON[kind]
  return (
    <div className="flex items-center gap-2 py-1">
      {Icon ? <Icon className="w-4 h-4 text-primary shrink-0" /> : null}
      <span className="text-sm truncate">{label}</span>
    </div>
  )
}
import { upsertNotificationRule, listProjects, listComponents, listChecks, listNotificationChannels } from "../api"

const CONDITION_OPTIONS = [
  { value: "downtime_continuous", label: "Continuous downtime", description: "Component down for N minutes straight." },
  { value: "downtime_cumulative", label: "Cumulative downtime", description: "Total downtime over a rolling window." },
  { value: "latency_threshold", label: "Latency threshold", description: "Avg latency above threshold for sustained period." },
  { value: "check_failure", label: "Consecutive check failures", description: "Any check fails N times in a row." },
  { value: "check_specific_failure", label: "Specific check failure", description: "A pinned check fails." },
]

const RuleEditor = ({ orgId, rule, onClose }) => {
  const isEdit = !!rule?._id
  const [name, setName] = useState(rule?.name || "")
  const [description, setDescription] = useState(rule?.description || "")
  const [enabled, setEnabled] = useState(rule?.enabled !== false)
  const [scope, setScope] = useState(rule?.scope || { projectId: null, componentId: null, checkId: null, appIdPattern: "" })
  const [condition, setCondition] = useState(() => {
    const PARAM_DEFAULTS = {
      downtime_continuous: { minSeconds: 300 },
      downtime_cumulative: { windowSeconds: 86400, totalSeconds: 1200 },
      latency_threshold:   { ms: 800, sustainedSeconds: 0 },
      check_failure:       { consecutiveFailures: 3 },
      check_specific_failure: {},
    }
    if (!rule?.condition) return { kind: "downtime_continuous", params: PARAM_DEFAULTS.downtime_continuous }
    const k = rule.condition.kind
    const p = rule.condition.params || {}
    // Backfill defaults for any missing keys (older rules saved with empty params).
    return { kind: k, params: { ...(PARAM_DEFAULTS[k] || {}), ...p } }
  })
  const [escalation, setEscalation] = useState(rule?.escalation || [{ afterSeconds: 0, label: "Initial" }])
  const [urgent, setUrgent] = useState(rule?.urgentMode?.enabled || false)
  const [refire, setRefire] = useState(rule?.urgentMode?.refireSeconds || 86400)
  const [autoResolveDays, setAutoResolveDays] = useState(rule?.autoResolveAfterDays || 0)
  const [allowAutoResolve, setAllowAutoResolve] = useState(rule?.allowAutoResolve !== false)
  const [subscribers, setSubscribers] = useState(rule?.subscribers || [])
  const [bypassAggregation, setBypassAggregation] = useState(rule?.bypassAggregation || false)
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState("rule")
  const TAB_DEFS = [
    { value: "rule", label: "Rule" },
    { value: "scope", label: "Scope" },
    { value: "escalation", label: "Escalation" },
    { value: "subscribers", label: "Subscribers" },
  ]

  const [projects, setProjects] = useState([])
  const [components, setComponents] = useState([])
  const [checks, setChecks] = useState([])
  const [channels, setChannels] = useState([])

  useEffect(() => {
    listProjects(orgId).then((r) => setProjects(r?.projects || []))
    listNotificationChannels(orgId).then((r) => setChannels(r?.channels || []))
  }, [orgId])

  useEffect(() => {
    if (!scope.projectId) { setComponents([]); return }
    listComponents(scope.projectId).then((r) => setComponents(r?.components || []))
  }, [scope.projectId])

  useEffect(() => {
    if (!scope.componentId) { setChecks([]); return }
    listChecks({ componentId: scope.componentId }).then((r) => setChecks(r?.checks || []))
  }, [scope.componentId])

  const projectOptions = [
    { value: "", label: "Any project", logo: null },
    ...projects.map((p) => ({ value: p._id, label: p.name, logo: p.logo, slug: p.slug })),
  ]
  const componentOptions = [{ value: "", label: "Any component" }, ...components.map((c) => ({ value: c._id, label: c.name }))]
  const checkOptions = checks.map((c) => ({ value: c._id, label: c.name || c.url, kind: c.kind }))
  const selectedCheckOptions = (scope.checkIds || []).map((id) => checkOptions.find((o) => o.value === String(id))).filter(Boolean)
  const inlineKindOptions = ["email", "slack", "sms", "voice"].map((k) => ({ value: k, label: k, kind: k }))

  const TARGET_HINTS = {
    email: { label: "Email address", placeholder: "ops@example.com" },
    slack: { label: "Slack webhook URL", placeholder: "https://hooks.slack.com/services/..." },
    sms:   { label: "Phone number",    placeholder: "+359..." },
    voice: { label: "Phone number",    placeholder: "+359..." },
  }

  // Combined channel dropdown options: inline kinds first, divider, then registered channels.
  const INLINE_LABELS = { email: "Email", slack: "Slack", sms: "SMS", voice: "Voice" }
  const channelDropdownOptions = [
    ...["email", "slack", "sms", "voice"].map((k) => ({ value: `inline:${k}`, label: INLINE_LABELS[k], kind: k, inline: true })),
    ...(channels.length > 0 ? [{ divider: true }] : []),
    ...channels.map((c) => ({ value: String(c._id), label: c.name, kind: c.kind })),
  ]

  const selectedChannelOption = (sub) => {
    if (sub.channelId) return channelDropdownOptions.find((o) => o.value === String(sub.channelId))
    if (sub.inlineKind) return channelDropdownOptions.find((o) => o.value === `inline:${sub.inlineKind}`)
    return null
  }

  const handleChannelChange = (i, opt) => {
    if (!opt) return
    if (opt.inline) {
      updateSubscriber(i, { channelId: null, inlineKind: opt.kind })
    } else {
      updateSubscriber(i, { channelId: opt.value, inlineKind: null, inlineTarget: "" })
    }
  }
  const conditionKind = CONDITION_OPTIONS.find((o) => o.value === condition.kind) || CONDITION_OPTIONS[0]

  const setParam = (k, v) => setCondition((c) => ({ ...c, params: { ...c.params, [k]: v } }))

  const addSubscriber = () => setSubscribers((s) => [...s, { channelId: null, inlineKind: "email", inlineTarget: "", label: "" }])
  const removeSubscriber = (i) => setSubscribers((s) => s.filter((_, idx) => idx !== i))
  const updateSubscriber = (i, patch) => setSubscribers((s) => s.map((x, idx) => idx === i ? { ...x, ...patch } : x))

  const onSave = async () => {
    setErr(null)
    setSaving(true)
    // Always ensure an initial step at 0s.
    const finalEscalation = escalation.length === 0
      ? [{ afterSeconds: 0, label: "Initial" }]
      : escalation
    const payload = {
      ruleId: isEdit ? rule._id : undefined,
      orgId,
      name, description, enabled,
      scope: {
        projectId: scope.projectId || null,
        componentId: scope.componentId || null,
        checkId: scope.checkId || null,
        appIdPattern: scope.appIdPattern || "",
      },
      condition,
      escalation: finalEscalation,
      urgentMode: { enabled: urgent, refireSeconds: Number(refire) || 86400 },
      allowAutoResolve,
      autoResolveAfterDays: Number(autoResolveDays) || 0,
      subscribers,
      bypassAggregation,
    }
    const r = await upsertNotificationRule(payload)
    setSaving(false)
    if (r?.error) return setErr(r.error)
    onClose()
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Edit rule" : "New notification rule"}
      variant="compact"
      className="max-w-2xl"
      closeOnClickOutside
      closeOnEscape
    >
      <div className="p-4 space-y-4">
        <div className="flex justify-end items-center gap-2 -mt-2">
          <span className="text-sm font-medium text-text_secondary">Enabled</span>
          <ToggleSwitch isOn={enabled} onChange={setEnabled} />
        </div>

        <Tabs tabs={TAB_DEFS} value={tab} onChange={setTab} />

        <div className="min-h-[420px]">
        {tab === "rule" && (
          <div className="space-y-4">
            <TextField label="Name" value={name} onChange={setName} />
            <TextField label="Description (optional)" value={description} onChange={setDescription} collapsible />

            <div>
              <label className="block mb-2 text-sm font-medium text-text_secondary">Condition</label>
              <DropdownSelect
                value={conditionKind}
                onChange={(v) => {
                  const k = (v?.[0] || v).value
                  const DEFAULTS = {
                    downtime_continuous: { minSeconds: 300 },
                    downtime_cumulative: { windowSeconds: 86400, totalSeconds: 1200 },
                    latency_threshold:   { ms: 800, sustainedSeconds: 0 },
                    check_failure:       { consecutiveFailures: 3 },
                    check_specific_failure: {},
                  }
                  setCondition({ kind: k, params: DEFAULTS[k] || {} })
                }}
                options={CONDITION_OPTIONS}
                renderOption={(opt) => (
                  <div className="py-1">
                    <div className="text-sm font-medium text-text">{opt.label}</div>
                    {opt.description && <div className="text-xs text-text_secondary mt-0.5">{opt.description}</div>}
                  </div>
                )}
              />
            </div>

            {condition.kind === "downtime_continuous" && (
              <TextField label="Minutes down (continuous)" type="number" value={String((condition.params?.minSeconds || 300) / 60)} onChange={(v) => setParam("minSeconds", Number(v) * 60)} />
            )}
            {condition.kind === "downtime_cumulative" && (
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Window (hours)" type="number" value={String((condition.params?.windowSeconds || 86400) / 3600)} onChange={(v) => setParam("windowSeconds", Number(v) * 3600)} />
                <TextField label="Total downtime (minutes)" type="number" value={String((condition.params?.totalSeconds || 1200) / 60)} onChange={(v) => setParam("totalSeconds", Number(v) * 60)} />
              </div>
            )}
            {condition.kind === "latency_threshold" && (() => {
              const sustainedOn = Number(condition.params?.sustainedSeconds || 0) > 0
              return (
                <div className="space-y-3">
                  <TextField label="Latency (ms)" type="number" value={String(condition.params?.ms || 800)} onChange={(v) => setParam("ms", Number(v))} />
                  <div className="flex items-center gap-3">
                    <ToggleSwitch
                      isOn={sustainedOn}
                      onChange={(v) => setParam("sustainedSeconds", v ? 300 : 0)}
                    />
                    <span className="text-sm font-medium">Sustained (avg over a window)</span>
                  </div>
                  <div className="text-xs text-text_secondary">
                    {sustainedOn
                      ? "Fires when the rolling average over the window meets or exceeds the threshold."
                      : "Peak detection: fires on any single sample at/above the threshold."}
                  </div>
                  {sustainedOn && (
                    <TextField label="Sustained for (minutes)" type="number" value={String((condition.params?.sustainedSeconds || 300) / 60)} onChange={(v) => setParam("sustainedSeconds", Number(v) * 60)} />
                  )}
                </div>
              )
            })()}
            {condition.kind === "check_failure" && (
              <TextField label="Consecutive failures" type="number" value={String(condition.params?.consecutiveFailures || 3)} onChange={(v) => setParam("consecutiveFailures", Number(v))} />
            )}
          </div>
        )}

        {tab === "scope" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block mb-2 text-sm font-medium text-text_secondary">Project</label>
                <DropdownSelect
                  value={projectOptions.find((o) => o.value === (scope.projectId || ""))}
                  onChange={(v) => setScope((s) => ({ ...s, projectId: (v?.[0] || v).value || null, componentId: null }))}
                  options={projectOptions}
                  searchEnabled
                  showCheckbox={false}
                  renderOption={(opt) => (
                    <div className="flex items-center gap-2 py-1">
                      {opt.logo ? (
                        <img src={opt.logo} alt="" className="w-6 h-6 rounded-md object-cover shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-md bg-neutral_1 flex items-center justify-center text-xs text-text_secondary shrink-0 uppercase">
                          {(opt.label || "?").slice(0, 1)}
                        </div>
                      )}
                      <span className="text-sm truncate">{opt.label}</span>
                    </div>
                  )}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-text_secondary">Component</label>
                <DropdownSelect
                  value={componentOptions.find((o) => o.value === (scope.componentId || ""))}
                  onChange={(v) => setScope((s) => ({ ...s, componentId: (v?.[0] || v).value || null, checkId: null }))}
                  options={componentOptions}
                  searchEnabled
                />
              </div>
            </div>

            {(condition.kind === "check_specific_failure" || scope.componentId) && (
              <div>
                <label className="block mb-2 text-sm font-medium text-text_secondary">
                  Checks {condition.kind === "check_specific_failure" ? "(pick one or more)" : "(optional, leave empty for any)"}
                </label>
                <DropdownSelect
                  value={selectedCheckOptions}
                  onChange={(v) => {
                    const arr = Array.isArray(v) ? v : [v]
                    setScope((s) => ({ ...s, checkIds: arr.map((x) => x.value).filter(Boolean), checkId: null }))
                  }}
                  options={checkOptions}
                  multiSelection
                  searchEnabled
                  showCheckbox
                  renderOption={(opt) => (
                    <div className="flex items-center gap-2 py-1">
                      {opt.kind && (
                        <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary shrink-0">{opt.kind}</span>
                      )}
                      <span className="text-sm truncate">{opt.label}</span>
                    </div>
                  )}
                />
                {condition.kind === "check_specific_failure" && !scope.componentId && (
                  <div className="text-xs text-text_secondary mt-1">Pick a component first to load its checks.</div>
                )}
              </div>
            )}

            <TextField label="App ID pattern (optional, supports *)" value={scope.appIdPattern || ""} onChange={(v) => setScope((s) => ({ ...s, appIdPattern: v }))} collapsible />
          </div>
        )}

        {tab === "escalation" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Typography variant="admin_h4">Escalation steps (from incident open)</Typography>
              <Button variant="secondary" onClick={() => setEscalation((s) => [...s, { afterSeconds: 3600, label: "1h reminder" }])}>+ Reminder</Button>
            </div>
            <div className="text-xs text-text_secondary">
              First step (0s) fires when the incident opens. Add more steps for reminders (e.g. 1h, 12h, 24h).
            </div>
            <DataTable
              data={escalation.map((s, i) => ({ ...s, _idx: i }))}
              itemIdField="_idx"
              renders={[
                {
                  id: "afterSeconds",
                  title: "After (sec)",
                  headerClassName: "w-32",
                  render: (_, step) => (
                    <input
                      type="number"
                      value={step.afterSeconds}
                      onChange={(e) => setEscalation((s) => s.map((x, idx) => idx === step._idx ? { ...x, afterSeconds: Number(e.target.value) } : x))}
                      className="w-full bg-neutral_1 border border-bordered rounded px-2 py-1 text-sm outline-none focus:border-primary"
                    />
                  ),
                },
                {
                  id: "label",
                  title: "Label",
                  render: (_, step) => (
                    <input
                      type="text"
                      value={step.label || ""}
                      onChange={(e) => setEscalation((s) => s.map((x, idx) => idx === step._idx ? { ...x, label: e.target.value } : x))}
                      className="w-full bg-neutral_1 border border-bordered rounded px-2 py-1 text-sm outline-none focus:border-primary"
                    />
                  ),
                },
                {
                  id: "actions",
                  title: "",
                  headerClassName: "w-12",
                  render: (_, step) => (
                    <button
                      onClick={() => setEscalation((s) => s.filter((_, idx) => idx !== step._idx))}
                      className="p-1 rounded hover:bg-error/10 text-error"
                      title="Remove"
                    ><TrashIcon className="w-5 h-5" /></button>
                  ),
                },
              ]}
              showSearch={false}
              showFooter={false}
              noData={null}
            />

            <div className="border-t border-bordered pt-3 space-y-3">
              <div className="flex items-center gap-3">
                <ToggleSwitch isOn={allowAutoResolve} onChange={setAllowAutoResolve} />
                <span className="text-sm font-medium">Allow auto resolve</span>
              </div>
              <div className="text-xs text-text_secondary -mt-1">
                When on, the incident closes automatically once the condition stays cleared for the stable window.
              </div>

              <TextField
                label="Forced auto resolve after (days, 0=disabled)"
                type="number"
                value={String(autoResolveDays)}
                onChange={(v) => setAutoResolveDays(Number(v))}
                collapsible
              />

              <div className="flex items-center gap-3">
                <ToggleSwitch isOn={urgent} onChange={setUrgent} />
                <span className="text-sm font-medium">Urgent mode (re-fire while open)</span>
              </div>
              {urgent && (
                <TextField label="Re-fire every (seconds)" type="number" value={String(refire)} onChange={(v) => setRefire(Number(v))} />
              )}
              <div className="flex items-center gap-3">
                <ToggleSwitch isOn={bypassAggregation} onChange={setBypassAggregation} />
                <span className="text-sm font-medium">Bypass aggregation/rate-limit</span>
              </div>
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="space-y-2">
            {subscribers.map((sub, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block mb-2 text-sm font-medium text-text_secondary">Channel</label>
                  <DropdownSelect
                    value={selectedChannelOption(sub)}
                    onChange={(v) => handleChannelChange(i, v?.[0] || v)}
                    options={channelDropdownOptions}
                    showCheckbox={false}
                    searchEnabled
                    renderOption={(opt) => <KindLine kind={opt.kind} label={opt.label} />}
                    onValueItemRender={(opt) => opt ? <KindLine kind={opt.kind} label={opt.label} /> : <span className="text-text_secondary text-sm">Pick a channel</span>}
                  />
                </div>
                {!sub.channelId && sub.inlineKind && (() => {
                  const hint = TARGET_HINTS[sub.inlineKind] || { label: "Target", placeholder: "" }
                  return (
                    <div className="flex-1">
                      <TextField
                        label={hint.label}
                        placeholderText={hint.placeholder}
                        value={sub.inlineTarget}
                        onChange={(v) => updateSubscriber(i, { inlineTarget: v })}
                      />
                    </div>
                  )
                })()}
                <button onClick={() => removeSubscriber(i)} className="p-2 rounded hover:bg-error/10 text-error mb-1"><TrashIcon className="w-5 h-5" /></button>
              </div>
            ))}
            <Button variant="secondary" onClick={addSubscriber}>+ Subscriber</Button>
          </div>
        )}
        </div>

        <div className="flex flex-col items-center gap-2 pt-2 border-t border-bordered">
          <Button onClick={onSave} disabled={saving} className="px-10 py-3 text-base">{saving ? "Saving..." : (isEdit ? "Save" : "Create")}</Button>
          {err && <div className="text-error text-sm">Error: {err}</div>}
        </div>
      </div>
    </Modal>
  )
}

export default RuleEditor
